import { useEffect } from 'react';
import type { RefObject } from 'react';
import { trackEvent } from './plausible';

/**
 * Fire Plausible's `Section View` once when a section is scrolled into view.
 *
 * Why this exists: the home page's body is almost entirely non-clickable —
 * hosts, heroes, prizes, and the call-to-rise are art and copy. Click events
 * therefore say nothing about whether anyone read past the hero. Scroll reach
 * is the only engagement signal the page actually produces, and it's the one
 * that answers "did the announcement land?".
 *
 * Deliberately independent of the motion layer: HomePage's `Reveal` skips its
 * IntersectionObserver entirely under `prefers-reduced-motion`, and those
 * visitors' scroll depth counts the same as everyone else's.
 *
 * Fires at most once per mount. Home is the only consumer and it unmounts on
 * navigation away, so "once per mount" is "once per page view" — matching the
 * tracker, which counts an SPA route change as a new page view.
 *
 * @param ref     element to observe (the section wrapper)
 * @param name    section slug — a closed set, see lib/plausible.ts
 * @param enabled pass false to skip a section that isn't part of this phase
 */
export function useSectionView(
  ref: RefObject<Element | null>,
  name: string | undefined,
  enabled = true,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || !name || !enabled) return;
    // SSR/jsdom guard — the harness scripts render without an observer.
    if (typeof IntersectionObserver === 'undefined') return;

    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (fired || !entries.some((e) => e.isIntersecting)) return;
        fired = true;
        trackEvent('Section View', { section: name });
        io.disconnect();
      },
      // Bottom margin, not a threshold ratio: several of these sections are
      // taller than a phone viewport, so any threshold above ~0 would never be
      // met on mobile and the event would silently never fire. This instead
      // means "the section's top has passed 75% of the viewport height" —
      // scrolled-to, at every size.
      { rootMargin: '0px 0px -25% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, name, enabled]);
}
